import { ReactElement, useContext, useState, KeyboardEvent } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { AutoCheckImage } from '../../../../ui-components/AutoCheckImage'
import { SearchStoreContext } from '../../../../store/search/SearchStore'
import { Button } from '../../../../ui-components/forms/Button'
import { TextInput } from '../../../../ui-components/forms/TextInput'
import {
  VehicleWidgetErrorState,
  vehicleWidgetStore,
} from '../store/VehicleWidgetStore'
import VinValidator from '../VinValidator'
import { VinCheckMessage } from './VinCheckMessage'
import { usePartsCatalog } from '../../../../store/partsCatalog/PartsCatalog'
import { breakpoints } from '../../../../theme/breakpoints'
import { I18n } from 'common'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'

export const VinSearch = (): ReactElement => {
  const navigate = useNavigate()
  const searchStore = useContext(SearchStoreContext)
  const catalog = usePartsCatalog()
  const [vin, setVin] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { isAutoCheckEnabled } = useUiStateStore()

  const handleSearchByVin = async () => {
    if (vin.length !== 17) {
      vehicleWidgetStore.setErrorState(
        VehicleWidgetErrorState.vinDigitCheckFailed
      )
      return
    }
    setIsLoading(true)
    const foundVehicle = await vehicleWidgetStore.lookupVehicle({ vin })
    setIsLoading(false)
    if (foundVehicle) {
      vehicleWidgetStore.setIsDrawerOpened(false)
      catalog.resetStore()
      navigate('/partsCatalog')
    }
    searchStore.setShowVehicleTransition(true)
  }

  const submitIfEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchByVin()
    }
  }

  const sanitizeAndSetVin = (text: string) => {
    const sanitized = VinValidator.sanitize(text)
    setVin(sanitized)
  }

  return (
    <Wrapper>
      <MainRow>
        <Col>
          <Row>
            <InputWrapper>
              <Label>
                <I18n name="vin" />
              </Label>
              <TextInput
                onChange={sanitizeAndSetVin}
                value={vin}
                onKeyPress={submitIfEnter}
                noMargin
                placeholder="enterVin"
                autoFocus={true}
                width="100%"
              />
              <VinCheckMessage vin={vin} />
            </InputWrapper>

            <SyltedButton
              onClick={handleSearchByVin}
              loading={isLoading}
              disabled={vin.length < 17}
              text="add"
              size="large"
            />
          </Row>
        </Col>
        {isAutoCheckEnabled() && <AutoCheckImage vin={vin} size="medium" />}
      </MainRow>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-grow: 1;
  width: 100%;
  .field-group-content {
    align-items: flex-start;
    width: 100%;
  }
`

const MainRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`

const Row = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  gap: 8px;

  @media screen and (width < ${breakpoints.mobileLarge}px) {
    flex-direction: column;
  }
`

const Col = styled.div`
  flex-grow: 1;
  flex-direction: column;
`

const InputWrapper = styled.div`
  width: 100%;
  max-width: 399px;
  @media screen and (width < ${breakpoints.mobileLarge}px) {
    max-width: none;
  }
`

const SyltedButton = styled(Button)`
  max-width: 128px;
  flex-grow: 1;
  margin: 0;
  align-self: start;
  @media screen and (width < ${breakpoints.mobileLarge}px) {
    width: 100%;
    max-width: none;
    margin-right: 0px;
  }
`

const Label = styled.div`
  display: none;
  font-weight: 700;
  font-size: 14px;
  @media screen and (width < ${breakpoints.mobileLarge}px) {
    display: block;
    margin-bottom: 3px;
  }
`
